import { Link } from 'react-router-dom';
import { site } from '../../site';
import { LegalPage } from './LegalPage';

export function TermsPage() {
  return (
    <LegalPage
      title="Terms of service"
      documentTitle="Terms — dala"
      updated={site.termsUpdated}
    >
      <p>
        These terms govern your use of the Dala app and website. By creating an
        account or using Dala you agree to them. If you do not agree, do not
        use the service.
      </p>

      <Section title="The service">
        <p>
          Dala connects customers in {site.region} with local drivers and
          merchants for rides, food delivery, store orders, papalit shopping,
          and parcels. Dala
          is a technology platform. Drivers and merchants are independent
          providers, not employees of Dala, unless we say otherwise in writing.
        </p>
        <p>
          The service area, available vehicle types, and features may change as
          the pilot develops. We may refuse, suspend, or stop offering the
          service in whole or in part.
        </p>
      </Section>

      <Section title="Community rules: zero tolerance for objectionable content and abuse">
        <p>
          Dala has no tolerance for objectionable content or abusive
          behaviour. Chat messages, photos, names, ratings and written
          feedback are all covered by this rule, whether you are a customer, a
          rider or a merchant.
        </p>
        <p>You must not send, post or share:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Threats of violence, or anything that intimidates or stalks another
            person.
          </li>
          <li>Harassment, bullying, or repeated unwanted contact.</li>
          <li>
            Hate speech or slurs targeting anyone’s race, ethnicity, religion,
            disability, sex, gender identity or sexual orientation.
          </li>
          <li>Sexual content, sexual advances, or nudity.</li>
          <li>Content that sexualises or endangers a child, in any form.</li>
          <li>
            Scams, fraud, impersonation, or attempts to move a transaction off
            Dala to avoid our protections.
          </li>
          <li>Spam, or any content that is illegal under Philippine law.</li>
        </ul>
      </Section>

      <Section title="How we enforce this">
        <p>
          Dala filters messages automatically before they are delivered. Text
          that matches our objectionable-content list is rejected and never
          reaches the other person.
        </p>
        <p>
          Anyone can report content from inside the app, and anyone can block
          another account. A block hides that account’s messages from you
          immediately, stops them contacting you, and keeps the two of you from
          being matched on future jobs.
        </p>
        <p>
          Every report and every block opens a case for our support team. We
          review each case and act on it within 24 hours. Where a report is
          upheld we remove the content and suspend the account that sent it. A
          suspended account cannot sign back in.
        </p>
        <p>
          We may act without a report if we find objectionable content
          ourselves, and we may report unlawful behaviour to the authorities.
        </p>
      </Section>

      <Section title="Accounts">
        <p>
          You must use a mobile number you control, and you must be at least
          18 years old to hold a Dala account. You are responsible for
          activity on your account. Keep your device secure. One person should
          not share an account in a way that misleads other users.
        </p>
      </Section>

      <Section title="Bookings, cash, and cancellations">
        <p>
          Fares and order totals shown in the app are estimates until the job is
          completed. The MABAMA pilot is cash on delivery — you pay the driver
          or rider when the trip or order is fulfilled. You agree to pay the
          amount due for completed jobs.
        </p>
        <p>
          Cancelling after a driver is assigned, or failing to appear, may lead
          to limits on your account. Do not use Dala for unlawful items or
          trips.
        </p>
      </Section>

      <Section title="Drivers and merchants">
        <p>
          Applying to drive or sell does not guarantee approval. You must provide
          accurate documents, follow local laws (including licensing and safety
          rules), and treat other users with respect. Dala may pause or remove
          access after complaints, safety issues, or policy breaches.
        </p>
      </Section>

      <Section title="Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Harass, threaten, or defraud other users or Dala staff.</li>
          <li>Interfere with the app, scrape it, or probe our systems.</li>
          <li>Misrepresent who you are or where a pickup or drop-off is.</li>
          <li>Use Dala if we have suspended your account.</li>
        </ul>
      </Section>

      <Section title="Privacy">
        <p>
          How we handle personal data is described in the{' '}
          <Link to="/privacy" className="font-bold text-terracotta hover:underline">
            Privacy policy
          </Link>
          .
        </p>
      </Section>

      <Section title="Disclaimers and liability">
        <p>
          Trips, deliveries, and store runs happen in the real world. We do not
          control traffic, weather, merchant stock, or how a driver performs a
          job. The app is provided as available. To the extent allowed by
          Philippine law, Dala is not liable for indirect or consequential
          losses, or for amounts beyond the fare or order value of the job in
          dispute.
        </p>
        <p>
          Nothing in these terms limits liability that cannot be limited by law,
          including for fraud or death or personal injury caused by negligence
          where such a limit is not permitted.
        </p>
      </Section>

      <Section title="Changes">
        <p>
          We may update these terms. Continued use after the “Last updated” date
          means you accept the new terms. The current version lives at this URL.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about these terms:{' '}
          <Link to="/support" className="font-bold text-terracotta hover:underline">
            Support
          </Link>{' '}
          or{' '}
          <a
            href={`mailto:${site.infoEmail}`}
            className="font-bold text-terracotta hover:underline"
          >
            {site.infoEmail}
          </a>
          .
        </p>
      </Section>
    </LegalPage>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl text-ink">{title}</h2>
      {children}
    </section>
  );
}
